import type {
  CreateOrganizationGpuJobInput,
  createOrganizationGpuTask,
  OrganizationGpuJobDto,
} from "@dutyon/apis";
import { type OrganizationGpuHistorySummary, summarizeOrganizationGpuHistory } from "@dutyon/apis";
import type { DutyData } from "@dutyon/ui-kit";
import dayjs from "dayjs";
import { deriveGpuChunkSeed } from "./autoGenerateGpuSchedule.ts";
import { mergeIntoDutyData, toFixedSchedule } from "./dutyScheduleData.ts";

import { diagnoseRemoteGpuBoundaries, type RemoteGpuBoundaryState } from "./remoteGpuBoundaries.ts";

type Task = ReturnType<typeof createOrganizationGpuTask>;
const reviewFailureMessage = "완료된 결과의 현재 접근 권한이나 생성 조건을 확인하지 못했습니다.";
type Storage = Pick<globalThis.Storage, "getItem" | "setItem" | "removeItem">;
export type RemoteGpuBatchPlan = Omit<
  CreateOrganizationGpuJobInput,
  "app" | "requestKey" | "duration" | "fixedSchedule" | "previousJobId"
> & {
  endDayString: string;
  baseData: DutyData;
  preserveExistingAssignments: boolean;
};
type Receipt = { id: string; requestKey: string; index: number };
type ManualSource = { requestId: string; responseId: string };
type Record = {
  version: 1 | 2;
  owner: string;
  context: string;
  source: string;
  createdAt: number;
  plan: RemoteGpuBatchPlan;
  keys: string[];
  completed: Receipt[];
  stopRequested: boolean;
  sourceCutover?: ManualSource & { from: number };
};
export type RemoteGpuBatchState = {
  phase: "idle" | "running" | "paused" | "complete" | "cancelled" | "failed";
  startedAt: number | null;
  message: string | null;
  completedChunks: number;
  totalChunks: number;
  canRestore: boolean;
  preview: DutyData | null;
  warningCount: number | null;
  staffingWarnings: {
    dayString: string;
    workShiftId: string;
    required: number;
    assigned: number;
  }[];
  warningsTruncated: boolean;
  boundary: RemoteGpuBoundaryState | null;
  history: OrganizationGpuHistorySummary | null;
  generation: {
    mode: "independent" | "chained";
    jobs: { id: string; previousJobId: string | null }[];
  } | null;
  task: ReturnType<Task["getSnapshot"]>;
};
function canonical(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(canonical);
  if (value && typeof value === "object")
    return Object.fromEntries(
      Object.entries(value)
        .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
        .map(([key, child]) => [key, canonical(child)]),
    );
  return value;
}
const uuid = /^[a-f0-9]{8}-[a-f0-9]{4}-4[a-f0-9]{3}-[89ab][a-f0-9]{3}-[a-f0-9]{12}$/i;
function dateKey(value: string) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value) || dayjs(value).format("YYYY-MM-DD") !== value)
    throw new Error("생성 기간을 확인해 주세요.");
  return value;
}
export function remoteGpuChunkCount(plan: RemoteGpuBatchPlan) {
  const days =
    dayjs(dateKey(plan.endDayString)).diff(dayjs(dateKey(plan.startDayString)), "day") + 1;
  if (!Number.isSafeInteger(days) || days < 1) throw new Error("생성 기간을 확인해 주세요.");
  return Math.ceil(days / 31);
}
/** Project one target period; only the prior cloud ID may supply generated history. */
export function remoteGpuChunkInput(
  plan: RemoteGpuBatchPlan,
  index: number,
  requestKey: string,
  previousJobId?: string,
): CreateOrganizationGpuJobInput {
  const total = remoteGpuChunkCount(plan);
  if (
    !Number.isSafeInteger(index) ||
    index < 0 ||
    index >= total ||
    !uuid.test(requestKey) ||
    (previousJobId !== undefined && (index === 0 || !uuid.test(previousJobId))) ||
    !Number.isSafeInteger(plan.sampleCount) ||
    plan.sampleCount < 1 ||
    plan.sampleCount > 10000 ||
    !Number.isSafeInteger(plan.seed) ||
    plan.seed < 0 ||
    plan.seed > 0xffffffff
  )
    throw new Error("생성 조건을 확인해 주세요.");
  const start = dayjs(plan.startDayString).add(index * 31, "day");
  const duration = Math.min(31, dayjs(plan.endDayString).diff(start, "day") + 1);
  const dates = new Set(
    Array.from({ length: duration }, (_, day) => start.add(day, "day").format("YYYY-MM-DD")),
  );
  const fixedSchedule = toFixedSchedule(plan.baseData, !plan.preserveExistingAssignments);
  const fixed: NonNullable<CreateOrganizationGpuJobInput["fixedSchedule"]> = {};
  for (const [day, shifts] of Object.entries(fixedSchedule)) {
    if (!dates.has(day)) continue;
    for (const [shift, ids] of Object.entries(shifts)) {
      if (!plan.workShiftIds.includes(shift)) continue;
      const targets = ids.filter((id) => plan.nurseIds.includes(id));
      if (targets.length) {
        fixed[day] ??= {};
        fixed[day][shift] = targets;
      }
    }
  }
  return {
    app: "backoffice",
    teamId: plan.teamId,
    requestKey,
    ...(plan.sourceRequestId === undefined ? {} : { sourceRequestId: plan.sourceRequestId }),
    ...(plan.sourceResponseId === undefined ? {} : { sourceResponseId: plan.sourceResponseId }),
    ...(previousJobId === undefined ? {} : { previousJobId: previousJobId.toLowerCase() }),
    startDayString: start.format("YYYY-MM-DD"),
    duration,
    nurseIds: [...plan.nurseIds],
    workShiftIds: [...plan.workShiftIds],
    requirements: Object.fromEntries(
      Object.entries(plan.requirements).filter(([day]) => dates.has(day)),
    ),
    fixedSchedule: fixed,
    rules: structuredClone(plan.rules),
    seed: deriveGpuChunkSeed(plan.seed, index),
    sampleCount: plan.sampleCount,
  };
}
/** Client-only batch orchestration. Only original edited cells, planning choices and cloud IDs are persisted, never newly generated previews. */
export function createRemoteGpuBatch(options: {
  owner: string;
  context: string;
  source: string;
  manualSource?: ManualSource;
  task: Task;
  storage?: Storage;
  load(id: string): Promise<OrganizationGpuJobDto>;
  isCurrent(): boolean;
  canSubmit?(): boolean;
  onComplete?(preview: DutyData): void;
  /** Release a successfully applied batch so the editor can start again without acknowledgement. */
  clearOnComplete?: boolean;
  newKey(): string;
  now?: () => number;
}) {
  const { task, storage } = options;
  const manualSource = options.manualSource ? { ...options.manualSource } : undefined;
  const now = options.now ?? Date.now;
  const storageKey = `dutyon:backoffice-gpu-batch:v1:${encodeURIComponent(options.owner)}:${encodeURIComponent(options.context)}`;
  let record: Record | null = null;
  let preview: DutyData | null = null;
  let warningCount = 0;
  let completedJobs: OrganizationGpuJobDto[] = [];
  let phase: RemoteGpuBatchState["phase"] = "idle";
  let message: string | null = null;
  let canRestore = !!storage;
  let disposed = false,
    busy = false;
  const listeners = new Set<() => void>();
  let snapshot: RemoteGpuBatchState;
  const publish = () => {
    snapshot = {
      phase,
      startedAt: record?.createdAt ?? null,
      message,
      completedChunks: record?.completed.length ?? 0,
      totalChunks: record?.keys.length ?? 0,
      preview: preview ? structuredClone(preview) : null,
      warningCount: preview ? warningCount : null,
      staffingWarnings: preview
        ? completedJobs.flatMap((job) =>
            (job.result?.warnings ?? []).flatMap((warning) =>
              warning.code === "staffing" && warning.workShiftId
                ? [
                    {
                      dayString: warning.dayString,
                      workShiftId: warning.workShiftId,
                      required:
                        job.input.requirements[warning.dayString]?.[warning.workShiftId] ?? 0,
                      assigned:
                        job.result?.schedule[warning.dayString]?.[warning.workShiftId]?.length ?? 0,
                    },
                  ]
                : [],
            ),
          )
        : [],
      warningsTruncated: !!preview && completedJobs.some((job) => job.result?.warningsTruncated),
      boundary: preview ? diagnoseRemoteGpuBoundaries(completedJobs) : null,
      history: preview ? summarizeOrganizationGpuHistory(completedJobs) : null,
      generation: record
        ? {
            mode: record.version === 2 ? "chained" : "independent",
            jobs: preview
              ? completedJobs.map((job) => ({
                  id: job.id,
                  previousJobId: job.input.previousJobId ?? null,
                }))
              : [],
          }
        : null,
      canRestore,
      task: task.getSnapshot(),
    };
    for (const listener of listeners) listener();
  };
  const remove = () => {
    try {
      storage?.removeItem(storageKey);
    } catch {
      canRestore = false;
    }
  };
  const reset = () => {
    task.clear();
    remove();
    record = null;
    completedJobs = [];
    preview = null;
    phase = "idle";
    message = null;
  };
  const save = () => {
    if (!storage || !record) {
      canRestore = false;
      return;
    }
    try {
      const raw = JSON.stringify(record);
      if (raw.length > 262144) throw new Error();
      storage.setItem(storageKey, raw);
    } catch {
      canRestore = false;
    }
  };
  if (storage) {
    try {
      const raw = storage.getItem(storageKey);
      if (raw) {
        if (raw.length > 262144) throw new Error();
        const value: Record = JSON.parse(raw);
        if (
          (value.version !== 1 && value.version !== 2) ||
          value.owner !== options.owner ||
          value.context !== options.context ||
          value.source !== options.source ||
          !Number.isSafeInteger(value.createdAt) ||
          value.createdAt > now() ||
          now() - value.createdAt > 86400000 ||
          !value.plan ||
          !Array.isArray(value.keys) ||
          value.keys.length !== remoteGpuChunkCount(value.plan) ||
          value.keys.some((key) => !uuid.test(key)) ||
          new Set(value.keys).size !== value.keys.length ||
          !Array.isArray(value.completed) ||
          value.completed.length > value.keys.length ||
          new Set(value.completed.map((item) => item.id)).size !== value.completed.length ||
          value.completed.some(
            (item, index) =>
              item.index !== index || !uuid.test(item.id) || item.requestKey !== value.keys[index],
          ) ||
          typeof value.stopRequested !== "boolean"
        )
          throw new Error();
        if (
          value.sourceCutover &&
          (!manualSource ||
            value.plan.sourceRequestId !== undefined ||
            value.plan.sourceResponseId !== undefined ||
            value.sourceCutover.requestId !== manualSource.requestId ||
            value.sourceCutover.responseId !== manualSource.responseId ||
            !Number.isSafeInteger(value.sourceCutover.from) ||
            value.sourceCutover.from < 0 ||
            value.sourceCutover.from > value.keys.length)
        )
          throw new Error();
        record = value;
        phase = "paused";
        message = "기존 기간별 작업을 확인한 뒤 이어서 진행해 주세요.";
      }
    } catch {
      remove();
      phase = "paused";
      message =
        "복구 정보가 만료되었거나 요청 조건이 변경되었습니다. 기존 접수를 먼저 확인해 주세요.";
    }
  }
  if (!record && task.getSnapshot().input) {
    phase = "paused";
    message =
      "기존 접수는 있으나 기간별 복구 정보가 없습니다. 접수 확인과 취소 후 새로 시작해 주세요.";
  }
  const current = () => !disposed && options.isCurrent();
  const pause = (text: string) => {
    phase = "paused";
    preview = null;
    message = text;
    publish();
  };
  const chunkInput = (index: number) => {
    if (!record) throw new Error();
    const previous =
      record.version === 2 && index > 0 ? record.completed[index - 1]?.id : undefined;
    if (record.version === 2 && index > 0 && !previous) throw new Error();
    const cutover = record.sourceCutover;
    const plan =
      cutover && index >= cutover.from
        ? {
            ...record.plan,
            sourceRequestId: cutover.requestId,
            sourceResponseId: cutover.responseId,
          }
        : record.plan;
    return remoteGpuChunkInput(plan, index, record.keys[index], previous);
  };
  const verifyPendingInput = () => {
    if (!record) throw new Error();
    const input = task.getSnapshot().input;
    if (!input) return;
    const index = record.keys.indexOf(input.requestKey);
    if (
      index < 0 ||
      index > record.completed.length ||
      JSON.stringify(canonical(input)) !== JSON.stringify(canonical(chunkInput(index)))
    )
      throw new Error();
  };
  const verify = (job: OrganizationGpuJobDto, index: number, receipt?: Receipt) => {
    if (!record) throw new Error();
    const expected = chunkInput(index);
    if (
      job.app !== "backoffice" ||
      job.backend !== "metal" ||
      job.teamId !== expected.teamId ||
      job.requestKey !== expected.requestKey ||
      job.input.startDayString !== expected.startDayString ||
      job.input.duration !== expected.duration ||
      JSON.stringify(canonical(job.input)) !== JSON.stringify(canonical(expected)) ||
      (receipt && job.id !== receipt.id) ||
      job.input.seed !== expected.seed ||
      job.input.sampleCount !== expected.sampleCount ||
      JSON.stringify(job.input.nurseIds) !== JSON.stringify(expected.nurseIds) ||
      JSON.stringify(job.input.workShiftIds) !== JSON.stringify(expected.workShiftIds)
    )
      throw new Error();
    return job;
  };
  async function rebuild() {
    if (!record) throw new Error();
    let data = structuredClone(record.plan.baseData);
    let warnings = 0;
    const verifiedJobs: OrganizationGpuJobDto[] = [];
    for (const receipt of record.completed) {
      const job = verify(await options.load(receipt.id), receipt.index, receipt);
      if (!current() || job.status !== "SUCCEEDED" || !job.result) throw new Error();
      data = mergeIntoDutyData(structuredClone(data), job.result.schedule);
      warnings += job.result.warningCount;
      verifiedJobs.push(job);
    }
    completedJobs = verifiedJobs;
    preview = data;
    warningCount = warnings;
  }
  async function advance() {
    while (current() && record && phase === "running") {
      if (record.stopRequested) {
        await task.cancel();
        phase = "cancelled";
        message = "다음 기간 생성을 중단했습니다. 이미 완료된 기간은 확인 후 적용할 수 있습니다.";
        save();
        publish();
        return;
      }
      if (options.canSubmit?.() === false) {
        pause("근무 요청이 변경되거나 확정되어 다음 기간을 생성하지 않습니다.");
        return;
      }
      const state = task.getSnapshot();
      if (state.phase === "rejected") {
        phase = "failed";
        message = state.message;
        publish();
        return;
      }
      const index = record.completed.length;
      if (index >= record.keys.length) {
        // Reauthorize every period before automatically changing the editor.
        // Completed refreshes do not enter advance again, so later edits stay intact.
        await rebuild();
        if (!current() || record.stopRequested || phase !== "running") return;
        if (options.canSubmit?.() === false) {
          pause("근무 요청이 변경되거나 확정되어 결과를 자동 반영하지 않았습니다.");
          return;
        }
        if (preview) options.onComplete?.(structuredClone(preview));
        phase = "complete";
        message = null;
        if (options.onComplete && options.clearOnComplete) reset();
        publish();
        return;
      }
      if (state.phase === "idle") {
        await task.start(chunkInput(index));
        if (!current()) return;
        if (record.stopRequested) await task.cancel();
        continue;
      }
      if (state.input?.requestKey !== record.keys[index]) {
        // A crash can occur after recording a completed receipt but before clearing the single-job receipt.
        const previous = record.completed.find(
          (receipt) => receipt.requestKey === state.input?.requestKey,
        );
        if (previous && state.job?.id === previous.id && state.phase === "terminal") {
          task.clear();
          continue;
        }
        pause("기존 접수와 기간별 복구 정보가 일치하지 않습니다. 접수 상태를 확인해 주세요.");
        return;
      }
      if (!state.job || state.phase === "disconnected" || state.phase === "submitting") {
        publish();
        return;
      }
      const job = verify(state.job, index);
      if (job.status === "QUEUED" || job.status === "RUNNING") {
        publish();
        return;
      }
      if (job.status !== "SUCCEEDED" || !job.result) {
        phase = job.status === "CANCELLED" ? "cancelled" : "failed";
        message = "기간별 생성이 중단되었습니다. 완료된 기간의 결과는 별도로 확인할 수 있습니다.";
        publish();
        return;
      }
      preview = mergeIntoDutyData(
        structuredClone(preview ?? record.plan.baseData),
        job.result.schedule,
      );
      warningCount += job.result.warningCount;
      completedJobs.push(structuredClone(job));
      record.completed.push({ id: job.id, requestKey: job.requestKey, index });
      save(); // Record the receipt before clearing it; duplicate completion cannot advance twice.
      task.clear();
      publish();
    }
  }
  async function run(work: () => Promise<void>) {
    if (busy || !current()) return;
    busy = true;
    try {
      await work();
    } catch {
      if (current()) pause("기존 작업의 권한과 연결 상태를 확인한 뒤 다시 진행해 주세요.");
    } finally {
      busy = false;
    }
  }
  const unsubscribe = task.subscribe(publish);
  publish();
  return {
    getSnapshot: () => snapshot,
    subscribe(listener: () => void) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    start(plan: RemoteGpuBatchPlan, regenerate = false) {
      return run(async () => {
        if (options.canSubmit?.() === false) return;
        if (regenerate && phase !== "complete") return;
        if ((record && !regenerate) || task.getSnapshot().phase !== "idle") throw new Error();
        const keys = Array.from({ length: remoteGpuChunkCount(plan) }, () => options.newKey());
        // Validate every chunk before allocating any cloud work.
        keys.forEach((key, index) => {
          remoteGpuChunkInput(plan, index, key);
        });
        if (new Set(keys).size !== keys.length || keys.some((key) => record?.keys.includes(key)))
          throw new Error();
        record = {
          version: 2,
          owner: options.owner,
          context: options.context,
          source: options.source,
          createdAt: now(),
          plan: structuredClone(plan),
          keys,
          completed: [],
          stopRequested: false,
        };
        preview = structuredClone(plan.baseData);
        warningCount = 0;
        completedJobs = [];
        phase = "running";
        message = null;
        save();
        publish();
        await advance();
      });
    },
    refresh() {
      return run(async () => {
        await task.refresh();
        if (record?.stopRequested) await task.cancel();
        if (phase === "running") await advance();
        else publish();
      });
    },
    resume() {
      return run(async () => {
        if (!record) {
          await task.retry();
          pause("기존 접수 확인 후 취소하거나 종료 상태를 확인해 주세요.");
          return;
        }
        await rebuild();
        if (!current()) return;
        if (options.canSubmit?.() === false) {
          pause("현재 근무 요청에서는 다음 기간을 생성할 수 없습니다.");
          return;
        }
        verifyPendingInput();
        // Preserve every possibly submitted input, including a missing single-job receipt
        // after a storage failure. Recover that key with its original input first. Only
        // later chunks gain the current editor's source; the original plan/keys stay intact.
        if (
          manualSource &&
          !record.sourceCutover &&
          record.plan.sourceRequestId === undefined &&
          record.plan.sourceResponseId === undefined
        ) {
          const pending = task.getSnapshot().input;
          record.sourceCutover = {
            ...manualSource,
            from: Math.min(
              record.keys.length,
              Math.max(
                record.completed.length,
                pending ? record.keys.indexOf(pending.requestKey) + 1 : record.completed.length + 1,
              ),
            ),
          };
          save();
        }
        await task.retry();
        if (!current()) return;
        phase = "running";
        message = null;
        await advance();
      });
    },
    retryFailed() {
      return run(async () => {
        const state = task.getSnapshot();
        if (
          !record ||
          record.stopRequested ||
          phase !== "failed" ||
          state.phase !== "terminal" ||
          !state.job ||
          !["FAILED", "TIMED_OUT"].includes(state.job.status) ||
          options.canSubmit?.() === false
        )
          return;
        // Reauthorize completed periods before keeping their preview and predecessor reference.
        await rebuild();
        if (!current()) return;
        verifyPendingInput();
        const index = record.completed.length;
        const key = options.newKey();
        if (!uuid.test(key) || record.keys.includes(key)) throw new Error();
        task.clear();
        record.keys[index] = key;
        save();
        phase = "running";
        message = null;
        publish();
        await advance();
      });
    },
    async cancel() {
      if (!current()) return;
      if (record) {
        record.stopRequested = true;
        save();
      }
      phase = "cancelled";
      message =
        "다음 기간 생성을 중단했습니다. 접수 여부가 불명확하면 기존 요청을 재확인해 주세요.";
      publish();
      await task.cancel();
      if (current()) publish();
    },
    async review() {
      if (!current() || !record || !record.completed.length || busy) return null;
      busy = true;
      try {
        await rebuild();
        if (!current()) return null;
        if (message === reviewFailureMessage) message = null;
        publish();
        return structuredClone(preview);
      } catch {
        if (current()) pause(reviewFailureMessage);
        return null;
      } finally {
        busy = false;
      }
    },
    clear() {
      if (busy || !current()) return;
      const state = task.getSnapshot();
      if (!["idle", "terminal", "rejected"].includes(state.phase)) {
        pause("접수 중인 작업을 먼저 확인하거나 취소해 주세요.");
        return;
      }
      reset();
      publish();
    },
    dispose(clearReceipt = false) {
      disposed = true;
      unsubscribe();
      task.dispose(clearReceipt);
      if (clearReceipt) remove();
      preview = null;
      completedJobs = [];
      listeners.clear();
    },
  };
}

/** Return the freshly authorized preview and the diagnosis that belongs to that exact review. */
export async function reviewRemoteGpuBatch(
  batch: Pick<ReturnType<typeof createRemoteGpuBatch>, "review" | "getSnapshot">,
) {
  const preview = await batch.review();
  if (!preview) return null;
  const state = batch.getSnapshot();
  return {
    preview,
    boundary: state.boundary,
    history: state.history,
    generation: state.generation,
    warningCount: state.warningCount,
    staffingWarnings: state.staffingWarnings,
    warningsTruncated: state.warningsTruncated,
    completedChunks: state.completedChunks,
  };
}
export function sameRemoteGpuReview(
  a: Awaited<ReturnType<typeof reviewRemoteGpuBatch>>,
  b: Awaited<ReturnType<typeof reviewRemoteGpuBatch>>,
) {
  return !!a && !!b && JSON.stringify(a) === JSON.stringify(b);
}
